import type { LanguageCode } from "../utils/translations";
import { SERVICE_LABELS_HI } from "./servicesConfigHindi";
import { SERVICE_LABELS_FR } from "./servicesConfigFrench";

export interface ServiceNode {
  id: string;
  label: string;
  seq?: number;
  status?: "live" | "coming-soon";
  children?: ServiceNode[];
}

export const servicesTree: ServiceNode[] = [
  {
    id: "automation",
    seq: 1,
    label: "Automate Business",
    status: "live",
    children: [
      {
        id: "emailAutomation",
        label: "Email Automation",
        status: "live",
        children: [
          {
            id: "emailCampaigns",
            label: "Campaigns",
            children: [
              { id: "createCampaign", label: "Create Campaign" },
              { id: "campaignReports", label: "Reports" },
            ],
          },
          {
            id: "emailTemplates",
            label: "Templates",
            children: [
              { id: "createTemplate", label: "Create Template" },
              { id: "templateLibrary", label: "Template Library" },
            ],
          },
        ],
      },
      {
        id: "whatsappAutomation",
        label: "WhatsApp Automation",
        status: "coming-soon",
        children: [
          {
            id: "broadcast",
            label: "Broadcast",
            children: [
              { id: "createBroadcast", label: "New Broadcast" },
              { id: "broadcastLogs", label: "Logs" },
            ],
          },
          {
            id: "autoReply",
            label: "Auto Reply",
            children: [
              { id: "createAutoReply", label: "Create Rule" },
              { id: "replyAnalytics", label: "Analytics" },
            ],
          },
        ],
      },
      {
        id: "leadAutomation",
        label: "Lead Automation",
        status: "coming-soon",
        children: [
          {
            id: "leadCapture",
            label: "Lead Capture",
            children: [
              { id: "forms", label: "Forms" },
              { id: "Leadintegrations", label: "Lead Integrations" },
            ],
          },
          {
            id: "followUps",
            label: "Follow Ups",
            children: [
              { id: "sequence", label: "Sequence Builder" },
              { id: "reminders", label: "Reminders" },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "mobileApps",
    seq: 3,
    label: "Mobile Apps",
    status: "live",
    children: [
      { id: "allApps", label: "All Apps" },
      { id: "appAnalytics", label: "App Analytics" },
    ],
  },
  {
    id: "social",
    seq: 2,
    label: "Social Media Growth",
    status: "live",
    children: [
      {
        id: "youtube",
        label: "YouTube",
        status: "live",
        children: [
          {
            id: "videoOptimization",
            label: "Video Optimization",
            status: "live",
            children: [
              { id: "tagExtractor", label: "Tag Extractor", status: "live" },
              { id: "seoSuggestions", label: "SEO Suggestions", status: "coming-soon" },
            ],
          },
          {
            id: "analytics",
            label: "Analytics",
            status: "live",
            children: [
              { id: "views", label: "Views & Watch Time", status: "live" },
              { id: "engagement", label: "Engagement Insights", status: "live" },
            ],
          },
          { id: "commander", label: "Command Center", status: "live" },
        ],
      },
      {
        id: "instagramGrowth",
        label: "Instagram",
        status: "coming-soon",
        children: [
          {
            id: "contentPlanning",
            label: "Content Planning",
            children: [
              { id: "calendar", label: "Content Calendar" },
              { id: "hashtagResearch", label: "Hashtag Research" },
            ],
          },
        ],
      },
      {
        id: "facebookGrowth",
        label: "Facebook",
        status: "live",
        children: [
          {
            id: "analytics",
            label: "Analytics",
            status: "live",
            children: [
              { id: "views", label: "Views & Watch Time", status: "live" },
              { id: "engagement", label: "Engagement Insights", status: "live" },
            ],
          },
          {
            id: "homepageFacebook",
            label: "Homepage Analytics",
            status: "live",
            children: [
              { id: "views", label: "Views & Watch Time", status: "live" },
              { id: "engagement", label: "Engagement Insights", status: "live" },
            ],
          },
        ],
      },
    ],
  },

  {
    id: "website",
    seq: 4,
    label: "Website Development",
    status: "live",
    children: [
      { id: "allWebsites", label: "All Websites", status: "live" },
      {
        id: "pages",
        label: "Pages",
        status: "coming-soon",
        children: [
          { id: "createPage", label: "Create Page" },
          { id: "editPage", label: "Edit Page" },
        ],
      },
      {
        id: "seo",
        label: "SEO Settings",
        children: [
          { id: "metaTags", label: "Meta Tags" },
          { id: "sitemap", label: "Sitemap" },
        ],
      },
    ],
  },

  {
    id: "tools",
    seq: 5,
    label: "Tools & Utilities",
    status: "live",
    children: [
      {
        id: "emailTools",
        label: "Email",
        status: "live",
        children: [
          { id: "emailValidator", label: "Email Validator", status: "live" },
          { id: "bulkEmailValidator", label: "Bulk Email Validator", status: "coming-soon" },
        ],
      },
      {
        id: "smsTools",
        label: "SMS",
        status: "coming-soon",
        children: [{ id: "smsSender", label: "Send SMS" }],
      },
    ],
  },
  {
    id: "integrations",
    seq: 6,
    label: "Integrations",
    status: "live",
    children: [
      { id: "facebookConnect", label: "Facebook", status: "live" },
      { id: "instagramConnect", label: "Instagram", status: "coming-soon" },
      { id: "youtubeConnect", label: "YouTube", status: "live" },
      { id: "whatsappConnect", label: "WhatsApp Business", status: "coming-soon" },
      { id: "googleBusiness", label: "Google Business Profile", status: "coming-soon" },
    ],
  },
];

/**
 * Returns a copy of the services tree with labels localized.
 * Node ids (used for navigation) are never changed.
 */
export const localizeServicesTree = (
  nodes: ServiceNode[],
  lang: LanguageCode
): ServiceNode[] => {
  const labels =
    lang === "hi"
      ? SERVICE_LABELS_HI
      : lang === "fr"
      ? SERVICE_LABELS_FR
      : undefined;

  if (!labels) return nodes;

  return nodes.map((node) => ({
    ...node,
    label: labels[node.id] ?? node.label,
    children: node.children
      ? localizeServicesTree(node.children, lang)
      : undefined,
  }));
};
